import React from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

export interface AdstockSaturationChartsProps {
  channels: Array<{ channel: string; adstockDecay: number; saturationHalfPoint: number; color: string }>
}

export function AdstockSaturationCharts({ channels }: AdstockSaturationChartsProps) {
  const adstockData = channels.map(channel => {
    const points: number[] = []
    let carryover = 100
    for (let week = 0; week <= 8; week++) {
      points.push(carryover)
      carryover *= channel.adstockDecay
    }
    return {
      name: channel.channel,
      data: points,
      color: channel.color,
    }
  })

  const adstockOptions: Highcharts.Options = {
    chart: { type: 'line', height: 250 },
    title: { text: 'Adstock Decay Curves' },
    xAxis: {
      title: { text: 'Weeks After Spend' },
      categories: ['0', '1', '2', '3', '4', '5', '6', '7', '8']
    },
    yAxis: {
      title: { text: 'Effect Remaining (%)' },
      min: 0,
      max: 100
    },
    tooltip: {
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        return `<b>${this.series.name}</b><br/>Week ${this.x}: ${(this.y as number).toFixed(1)}% effect remaining`
      }
    },
    series: adstockData as Highcharts.SeriesOptionsType[],
    credits: { enabled: false },
  }

  const saturationData = channels.map(channel => {
    const points: [number, number][] = []
    const maxSpend = channel.saturationHalfPoint * 3
    for (let spend = 0; spend <= maxSpend; spend += maxSpend / 20) {
      const beta = 0.8
      const effect = Math.pow(spend, beta) / (Math.pow(spend, beta) + Math.pow(channel.saturationHalfPoint, beta))
      points.push([spend, effect * 100])
    }
    return {
      name: channel.channel,
      data: points,
      color: channel.color,
    }
  })

  const saturationOptions: Highcharts.Options = {
    chart: { type: 'line', height: 250 },
    title: { text: 'Saturation Curves (Diminishing Returns)' },
    xAxis: {
      title: { text: 'Weekly Spend ($)' },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return `$${((this.value as number) / 1000).toFixed(0)}k`
        }
      }
    },
    yAxis: {
      title: { text: 'Effect (% of Max)' },
      min: 0,
      max: 100
    },
    tooltip: {
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        return `<b>${this.series.name}</b><br/>Spend $${((this.x as number) / 1000).toFixed(1)}k: ${(this.y as number).toFixed(1)}% of max effect`
      }
    },
    series: saturationData as Highcharts.SeriesOptionsType[],
    credits: { enabled: false },
  }

  return (
    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
      <HighchartsReact highcharts={Highcharts} options={adstockOptions} />
      <HighchartsReact highcharts={Highcharts} options={saturationOptions} />
    </div>
  )
}
